/**
 * Reusable stateless form component for Scene
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// import third-party libraries
import { EditorState, convertToRaw } from 'draft-js';

// import form components
import {
  CdnPathInput
  , CheckboxInput
  , RadioInput
  , RichEditor
  , TextAreaInput
  , TextInput
  , UrlInput
} from '../../../global/components/forms';

// import module components
import VideoTrackListItem from './VideoTrackListItem.js.jsx';

function BuildSceneForm({
  addSceneTrack
  , cancelLink
  , deleteTrack
  , formType
  , handleFormChange
  , handleTrackFormChange
  , sceneMediaType
  , isNewChartContent
  , isNewDescriptionContent
  , isNewTranscriptContent
  , scene
}) {
  return (
    <div className="form-container">
      <form name="sceneForm" className="scene-form" >
        <div className="-form-step"><span className="-num">1</span> General info</div>
        <TextInput
          change={handleFormChange}
          label="Title"
          name="title"
          placeholder="Title"
          required={true}
          value={scene.title}
        />
        <TextAreaInput
          cols={100}
          change={handleFormChange}
          helpText={<span>{scene.summary.length} of 180 </span>}
          label="Scene summary"
          maxLength="180"
          name="summary"
          required={false}
          rows="3"
          value={scene.summary}
        />
        <RadioInput
          helpText={sceneMediaType === '' ? "'Neither' means we need to be sure to build a custom follow up question" : null}
          label="Does this scene have a video or PDF?"
          options={[
            {val: 'video', display: 'Video'},
            {val: 'pdf', display: 'PDF'},
            {val: '', display: 'Neither'},
          ]}
          name="sceneMediaType"
          value={sceneMediaType}
          change={handleFormChange}
          inLine={true}
        />
        { sceneMediaType === 'video' ?
          <div>
            <CheckboxInput
              change={handleFormChange}
              value={scene.transparentOverlay}
              label={"Transparent Overlay"}
              name={"transparentOverlay"}
              helpText={"If checked, will show an overlay over top of the video's poster url or last frame. Otherwise default to white background."}
            />
            <CdnPathInput
              allowType="video"
              change={handleFormChange}
              helpText={"i.e. 'https://redstone.com/visits/123.mp4'"}
              label="Video Path"
              name="videoPath"
              required={false}
              validate={false}
              value={scene.videoPath}
            />
            <CdnPathInput
              change={handleFormChange}
              helpText={<span><strong>NOTE:</strong> this is only for displaying static image at the end of the video. Will use default if left blank.</span>}
              label="Video paused poster Path"
              name="posterPath"
              required={false}
              validate={false}
              value={scene.posterPath}
            />
          </div>
          :
          sceneMediaType === 'pdf' ?
          <CdnPathInput
            allowType="document"
            change={handleFormChange}
            label="PDF Path"
            name="pdfPath"
            required={false}
            type='document'
            validate={false}
            value={scene.pdfPath}
          />
          :
          null
        }

        <div className="-form-step"><span className="-num">2</span> Add overlay text and/or a custom follow up question</div>
        <p><em>Will default to "Please select from the action items to continue" if left blank</em></p>
        <TextAreaInput
          cols={100}
          change={handleFormChange}
          helpText={<div className="yt-row space-between"><span>This will show up on the left of the follow-up nodes</span><span>{scene.overlayText.length} of 300 </span></div>}
          label="Left-hand overlay text"
          maxLength="300"
          name="overlayText"
          required={false}
          rows="3"
          value={scene.overlayText}
        />
        <TextAreaInput
          cols={100}
          change={handleFormChange}
          helpText={<div className="yt-row space-between"><span>Shows up above followup nodes</span><span>{scene.followupQuestion.length} of 180 </span></div>}
          label="Custom followup question"
          maxLength="180"
          name="followupQuestion"
          required={false}
          rows="3"
          value={scene.followupQuestion}
        />
        <div className="-form-step"><span className="-num">3</span> Add a chart</div>
        <p><em>This shows up below the video. </em></p>
        <RichEditor
          change={handleFormChange}
          isNewContent={isNewChartContent}
          label="Chart for reference"
          name="chart"
          required={false}
          value={scene.chart && scene.chart.blocks && scene.chart.blocks.length > 0 ? scene.chart : convertToRaw(EditorState.createEmpty().getCurrentContent())}
        />
        <div className="-form-step"><span className="-num">4</span> Add a detailed scene description</div>
        <p><em>This shows up below the video. Can be thought of as the accompanying article to the video.</em></p>
        <RichEditor
          change={handleFormChange}
          isNewContent={isNewDescriptionContent}
          label="Detailed scene description"
          name="description"
          required={false}
          value={scene.description && scene.description.blocks && scene.description.blocks.length > 0 ? scene.description : convertToRaw(EditorState.createEmpty().getCurrentContent())}
        />
         <div className="-form-step"><span className="-num">5</span> Script</div>
         {/* <p><em>TODO: Add some copy here to help authoring out</em></p> */}
         <TextAreaInput
          cols={100}
          change={handleFormChange}
          label="Script"
          maxLength="300"
          name="script"
          required={false}
          rows="3"
          value={scene.script}
        />
        <div className="-form-step"><span className="-num">6</span> Transcript & subtitles</div>
        <RichEditor
          change={handleFormChange}
          isNewContent={isNewTranscriptContent}
          label="Transcript for reference"
          name="transcript"
          required={false}
          value={scene.transcript && scene.transcript.blocks && scene.transcript.blocks.length > 0 ? scene.transcript : convertToRaw(EditorState.createEmpty().getCurrentContent())}
        />

        <div className="input-group">
          <label>Subtitle Tracks</label>
          <div className="-subtitle-tracks">
            { scene.tracks.map((track, i) =>
              <VideoTrackListItem
                deleteTrack={deleteTrack}
                formType={formType}
                handleTrackFormChange={handleTrackFormChange}
                index={i}
                key={i}
                track={track}
              />
            )}
          </div>
          <button type="button" className="yt-btn x-small link success" onClick={() => addSceneTrack(formType)} style={{marginRight:20}}>+ Add track file</button>
        </div>
      </form>
    </div>
  )
}

BuildSceneForm.propTypes = {
  addSceneTrack: PropTypes.func
  , cancelLink: PropTypes.func
  , deleteTrack: PropTypes.func
  , formType: PropTypes.string
  , handleFormChange: PropTypes.func.isRequired
  , handleTrackFormChange: PropTypes.func
  , sceneHasVideo: PropTypes.string
  , isNewChartContent: PropTypes.bool.isRequired
  , isNewDescriptionContent: PropTypes.bool.isRequired
  , isNewTranscriptContent: PropTypes.bool.isRequired
  , scene: PropTypes.object
}

BuildSceneForm.defaultProps = {
  formType: 'new'
  , sceneHasVideo: 'yes'
}

export default BuildSceneForm;



// WEBPACK FOOTER //
// modules/scene/components/BuildSceneForm.js.jsx